import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Badge, Spinner, Table } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';

const OrderSettings = () => {
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState('');
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [collectionFilter, setCollectionFilter] = useState(''); // '' = all collections
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Order numbering: which season new order numbers are issued for.
  const [numberingSeason, setNumberingSeason] = useState('');
  const [numberingSeries, setNumberingSeries] = useState('');
  const [nextOrderId, setNextOrderId] = useState('');
  const [savingNumbering, setSavingNumbering] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/settings/seasons`);
        const list = res.data.seasons || [];
        setSeasons(list);
        if (list.length) setSeason(list[0].season);
      } catch (e) {
        toast.error('Failed to load seasons');
      } finally {
        setLoadingSeasons(false);
      }
    })();

    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/settings/order-series`);
        setNumberingSeason(res.data.season || '');
        setNumberingSeries(res.data.series || '');
        setNextOrderId(res.data.next_order_id || '');
      } catch (e) {
        toast.error('Failed to load order numbering');
      }
    })();
  }, []);

  // "SS'27" -> "27" (mirrors the server-side rule so the preview is instant).
  const seriesFromSeason = (name) => {
    const digits = String(name || '').replace(/\D/g, '');
    return digits.length >= 2 ? digits.slice(-2) : '';
  };

  const onNumberingSeasonChange = (name) => {
    setNumberingSeason(name);
    const derived = seriesFromSeason(name);
    if (derived) setNumberingSeries(derived);
  };

  const saveNumbering = async () => {
    setSavingNumbering(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/api/settings/order-series`, {
        season: numberingSeason || null,
        series: numberingSeries,
      });
      setNumberingSeries(res.data.series);
      setNextOrderId(res.data.next_order_id || '');
      toast.success(`New orders will be numbered FARAWS${res.data.series}xx`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save order numbering');
    } finally {
      setSavingNumbering(false);
    }
  };

  const loadSeason = async (s) => {
    if (!s) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/settings/season-products`, { params: { season: s } });
      const prods = res.data.products || [];
      setProducts(prods);
      setSelected(new Set(prods.filter((p) => p.selected).map((p) => p.id)));
    } catch (e) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (season) loadSeason(season); }, [season]);

  // Distinct collections present, for the browse filter.
  const collections = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const c = (p.collection || '').trim();
      if (c && !map.has(c.toLowerCase())) map.set(c.toLowerCase(), c);
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const col = collectionFilter.trim().toLowerCase();
    return products.filter((p) => {
      if (col && (p.collection || '').toLowerCase() !== col) return false;
      if (!q) return true;
      return [p.title, p.sku, p.collection].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [products, search, collectionFilter]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllShown = () => setSelected((prev) => { const n = new Set(prev); filtered.forEach((p) => n.add(p.id)); return n; });
  const clearAllShown = () => setSelected((prev) => { const n = new Set(prev); filtered.forEach((p) => n.delete(p.id)); return n; });

  const allShownSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/settings/season-products`, { season, product_ids: Array.from(selected) });
      toast.success(`Saved ${selected.size} product(s) for ${season}`);
      setSeasons((prev) => prev.map((s) => (s.season === season ? { ...s, curated_count: selected.size } : s)));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Order Settings" parent="Settings" title="Order Settings" />
      <Container fluid={true}>
        <Card>
          <CardHeader>
            <h4>Order Numbering</h4>
            <span>
              Order numbers are <code>FARAWS&lt;season&gt;&lt;number&gt;</code> — e.g. <code>FARAWS2701</code> is the
              first order of season 27. Switch the season here when a new one opens and numbering restarts
              at 01 in the new series.
            </span>
          </CardHeader>
          <CardBody>
            <Row className="align-items-end g-3">
              <Col md="4">
                <Label className="form-label">Current order season</Label>
                <Input type="select" value={numberingSeason} onChange={(e) => onNumberingSeasonChange(e.target.value)}>
                  <option value="">— Not set —</option>
                  {seasons.map((s) => <option key={s.season} value={s.season}>{s.season}</option>)}
                  {numberingSeason && !seasons.some((s) => s.season === numberingSeason) && (
                    <option value={numberingSeason}>{numberingSeason}</option>
                  )}
                </Input>
              </Col>
              <Col md="2">
                <Label className="form-label">Series</Label>
                <Input
                  value={numberingSeries}
                  onChange={(e) => setNumberingSeries(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  placeholder="27"
                  maxLength={2}
                />
              </Col>
              <Col md="3">
                <Label className="form-label">Next order number</Label>
                <div><Badge color="primary" className="f-14">{nextOrderId || '—'}</Badge></div>
              </Col>
              <Col md="3" className="text-md-end">
                <Button color="primary" onClick={saveNumbering} disabled={savingNumbering || !numberingSeries}>
                  {savingNumbering ? 'Saving…' : 'Save Numbering'}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h4>Order Sheets</h4>
            <span>Pick an order sheet, then choose which products appear in it for customers. Browse products from any collection using the filter. Leave a sheet empty to show the full catalogue.</span>
          </CardHeader>
          <CardBody>
            {loadingSeasons ? (
              <div className="text-center py-4"><Spinner size="sm" /> <span className="text-muted ms-1">Loading order sheets…</span></div>
            ) : (
              <Row className="align-items-end g-3 mb-3">
                <Col md="4">
                  <Label className="form-label">Order Sheet Name</Label>
                  <Input type="select" value={season} onChange={(e) => setSeason(e.target.value)}>
                    {seasons.map((s) => (
                      <option key={s.season} value={s.season}>
                        Order Sheet {s.season} ({s.curated_count} selected)
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="3">
                  <Label className="form-label">Filter by collection</Label>
                  <Input type="select" value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)}>
                    <option value="">All collections</option>
                    {collections.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Input>
                </Col>
                <Col md="3">
                  <Label className="form-label">Search products</Label>
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by SKU, title…" />
                </Col>
                <Col md="2" className="text-md-end">
                  <Badge color="primary" className="me-2">{selected.size} in sheet</Badge>
                </Col>
              </Row>
            )}

            {!loadingSeasons && (
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <Button color="light" size="sm" onClick={selectAllShown} disabled={loading || !filtered.length}>Select all shown</Button>
                <Button color="light" size="sm" onClick={clearAllShown} disabled={loading || !filtered.length}>Clear all shown</Button>
                <span className="text-muted f-12 ms-1">{filtered.length} shown</span>
                <div className="ms-auto">
                  <Button color="primary" onClick={save} disabled={saving || loading}>{saving ? 'Saving…' : 'Save Selection'}</Button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5"><Spinner size="sm" /> <span className="text-muted ms-1">Loading products…</span></div>
            ) : (
              <div className="table-responsive" style={{ maxHeight: 560, overflowY: 'auto' }}>
                <Table className="align-middle mb-0" hover>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: 44 }}>
                        <Input type="checkbox" checked={allShownSelected} onChange={() => (allShownSelected ? clearAllShown() : selectAllShown())} />
                      </th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Collection</th>
                      <th className="text-end">WS €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => toggle(p.id)}>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                        </td>
                        <td>
                          <div className="fw-semibold">{p.title || '—'}</div>
                          {p.colour && <div className="text-muted f-12">{p.colour}</div>}
                        </td>
                        <td>{p.sku}</td>
                        <td><Badge color="secondary">{p.collection || '—'}</Badge></td>
                        <td className="text-end">{p.wholesale_price ?? '—'}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan="5" className="text-center text-muted py-4">No products match your search.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default OrderSettings;
